import { useEffect, useMemo, useRef, useState } from 'react';
import { categoryOptions, formatCategory, slugifyCategory } from '../lib/venue-categories';
import { promptDialog } from '../lib/dialogs';

interface Props {
  value: string[];
  onChange: (next: string[]) => void;
  /** Disable interaction (e.g. while saving). */
  disabled?: boolean;
  /** Used for `aria-label` on the toggle button. */
  ariaLabel: string;
  /** Bootstrap size modifier ('sm' for compact admin table rows). */
  size?: 'sm';
  /** Optional id to associate with an external `<label>`. */
  id?: string;
}

/**
 * Accessible multi-select for the venue category field. Renders a button that
 * shows the selected categories as Bootstrap pills, plus a popover with one
 * checkbox per known category and an "Add custom…" prompt.
 */
export default function VenueCategoryMultiSelect({
  value,
  onChange,
  disabled,
  ariaLabel,
  size,
  id,
}: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Close when clicking outside or pressing Escape; restore focus to the toggle.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Show the canonical list plus any custom values already on this venue,
  // so legacy custom slugs remain selectable.
  const options = useMemo(() => categoryOptions(value), [value]);

  const selected = useMemo(() => new Set(value), [value]);

  const toggle = (slug: string) => {
    if (selected.has(slug)) {
      onChange(value.filter((c) => c !== slug));
    } else {
      onChange([...value, slug]);
    }
  };

  const addCustom = async () => {
    const raw = await promptDialog({
      title: 'Add venue category',
      label: 'Category name',
      body: 'e.g. "comedy club", "music venue". Will be saved as lowercase + underscores.',
    });
    if (raw == null) return;
    const slug = slugifyCategory(raw);
    if (!slug || selected.has(slug)) return;
    onChange([...value, slug]);
  };

  const clear = () => {
    if (!value.length) return;
    onChange([]);
  };

  const buttonClass = ['form-select', size === 'sm' ? 'form-select-sm' : '', 'text-start']
    .filter(Boolean)
    .join(' ');

  return (
    <div className="position-relative" ref={rootRef}>
      <button
        ref={buttonRef}
        type="button"
        id={id}
        className={buttonClass}
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {value.length === 0 ? (
          <span className="text-muted">— None —</span>
        ) : (
          <span className="d-inline-flex flex-wrap gap-1 align-items-center">
            {value.map((c) => (
              <span key={c} className="badge text-bg-secondary fw-normal">
                {formatCategory(c)}
              </span>
            ))}
          </span>
        )}
      </button>

      {open && (
        <div
          className="border bg-body shadow-sm rounded position-absolute mt-1 p-2"
          style={{ zIndex: 1050, minWidth: '14rem', maxHeight: '18rem', overflowY: 'auto' }}
          role="listbox"
          aria-multiselectable="true"
          aria-label={ariaLabel}
        >
          {options.map((c) => {
            const checked = selected.has(c.value);
            const inputId = `mhcat-${id ?? ariaLabel.replace(/\s+/g, '-')}-${c.value}`;
            return (
              <div key={c.value} className="form-check mb-1">
                <input
                  id={inputId}
                  className="form-check-input"
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(c.value)}
                />
                <label className="form-check-label small" htmlFor={inputId}>
                  {c.label}
                </label>
              </div>
            );
          })}
          <hr className="my-2" />
          <div className="d-flex justify-content-between gap-2">
            <button
              type="button"
              className="btn btn-link btn-sm p-0 text-decoration-none"
              onClick={addCustom}
            >
              + Add custom…
            </button>
            <button
              type="button"
              className="btn btn-link btn-sm p-0 text-decoration-none text-muted"
              onClick={clear}
              disabled={value.length === 0}
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
